import React, { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useBills, useIncomes, usePayments } from "../lib/hooks";
import { formatMoney, monthRange, occurrencesInRange } from "../lib/finance";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

/**
 * Newspaper PDF export — a print-styled single-page ledger.
 * User invokes via window.print(). All layout is print-optimized.
 */
export default function PdfExportPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const currency = user?.prefs?.currency || "USD";

  const now = new Date();
  const y = parseInt(params.get("y") || now.getFullYear(), 10);
  const m = parseInt(params.get("m") ?? now.getMonth(), 10);

  const { data: bills = [] } = useBills();
  const { data: incomes = [] } = useIncomes();
  const { data: payments = [] } = usePayments();
  const { start, end } = monthRange(y, m);
  const monthLabel = start.toLocaleString("default", { month: "long", year: "numeric" });

  const paidSet = useMemo(
    () => new Set(payments.filter((p) => p.paid).map((p) => `${p.bill_id}_${p.occurrence_date}`)),
    [payments]
  );

  const rows = useMemo(() => {
    const list = [];
    for (const inc of incomes) {
      for (const d of occurrencesInRange(inc.first_date, inc.frequency, start, end)) {
        list.push({ date: d, type: "income", name: inc.name, category: inc.category || "Income", amount: Number(inc.amount || 0), paid: true });
      }
    }
    for (const b of bills) {
      for (const d of occurrencesInRange(b.first_due_date, b.frequency, start, end)) {
        list.push({ date: d, type: "bill", name: b.name, category: b.category, amount: -Number(b.amount || 0), paid: paidSet.has(`${b.bill_id}_${d}`) });
      }
    }
    list.sort((a, b) => a.date.localeCompare(b.date));
    let running = 0;
    return list.map((r) => { running += r.amount; return { ...r, running }; });
  }, [bills, incomes, paidSet, start, end]);

  const totals = rows.reduce((acc, r) => {
    if (r.type === "income") acc.income += r.amount;
    else acc.bills += -r.amount;
    return acc;
  }, { income: 0, bills: 0 });

  return (
    <div className="bg-white text-black min-h-screen">
      <div className="no-print px-4 py-3 border-b bg-[hsl(var(--background))] text-foreground flex items-center justify-between">
        <Button variant="ghost" onClick={()=>nav("/calendar")}><ArrowLeft size={14} className="mr-1"/> Back</Button>
        <Button onClick={()=>window.print()} data-testid="print-ledger-btn"><Printer size={14} className="mr-1"/> Print / Save as PDF</Button>
      </div>

      <div className="max-w-[820px] mx-auto py-10 px-8 font-newspaper" style={{ color: "#141210" }}>
        {/* Masthead */}
        <div className="text-center border-b-4 border-black pb-4">
          <div className="text-[10px] uppercase tracking-[0.5em]">Vol. I · No. 1 · Est. 2026 · Priceless</div>
          <h1 className="font-display text-6xl font-black tracking-tight mt-2">The Daily Ledger</h1>
          <div className="text-sm italic mt-1">A Monthly Bill Almanac · {monthLabel}</div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-center py-1 border-b border-black">
          {user?.name || user?.email} · Currency {currency} · Printed {new Date().toLocaleDateString()}
        </div>

        {/* Two-column masthead summary */}
        <div className="grid grid-cols-3 gap-6 mt-6 pb-6 border-b border-black">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold">Total Inflow</div>
            <div className="font-mono text-2xl font-bold">{formatMoney(totals.income, currency)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold">Total Outflow</div>
            <div className="font-mono text-2xl font-bold">{formatMoney(totals.bills, currency)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold">Ending Net</div>
            <div className="font-mono text-2xl font-bold">{formatMoney(totals.income - totals.bills, currency)}</div>
          </div>
        </div>

        {/* Ledger table */}
        <h2 className="font-display text-2xl font-bold mt-6 mb-3 pb-1 border-b-2 border-black">The Full Ledger</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="text-left py-1 pr-2">Date</th>
              <th className="text-left py-1 pr-2">Item</th>
              <th className="text-left py-1 pr-2">Category</th>
              <th className="text-left py-1 pr-2">Status</th>
              <th className="text-right py-1 pr-2">Amount</th>
              <th className="text-right py-1">Running</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-dashed" style={{ borderColor: "#8a7a5c" }}>
                <td className="py-1 pr-2 font-mono">{r.date.slice(5)}</td>
                <td className="py-1 pr-2 font-semibold">{r.name}</td>
                <td className="py-1 pr-2 italic">{r.category}</td>
                <td className="py-1 pr-2">{r.type === "income" ? "◆ Payday" : (r.paid ? "✓ Paid" : "· Due")}</td>
                <td className="py-1 pr-2 font-mono text-right">{formatMoney(r.amount, currency)}</td>
                <td className="py-1 font-mono text-right font-bold">{formatMoney(r.running, currency)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center italic py-4 text-[#6b5c48]">— No entries this month —</td></tr>
            )}
          </tbody>
        </table>

        <div className="text-[10px] uppercase tracking-[0.4em] text-center mt-8 pt-4 border-t border-black">— End of {monthLabel} Ledger —</div>
        <div className="text-center text-xs italic mt-2" style={{ color: "#6b5c48" }}>Set in Fraunces & JetBrains Mono. Printed on virtual parchment.</div>
      </div>
    </div>
  );
}
